import { Settings } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { cn } from '@/helpers/utils';
import type { NotificationFilter } from '@/types/notifications';

export type NotificationHeaderProps = {
  filter: NotificationFilter;
  onFilterChange: (f: NotificationFilter) => void;
  unreadCount: number;
  onMarkAllAsRead: () => void;
  isMarkingAll: boolean;
  onOpenSettings: () => void;
};

const FILTERS: NotificationFilter[] = ['all', 'unread'];

export function NotificationHeader({
  filter,
  onFilterChange,
  unreadCount,
  onMarkAllAsRead,
  isMarkingAll,
  onOpenSettings,
}: NotificationHeaderProps) {
  const canMarkAll = unreadCount > 0;

  const handleTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>, current: NotificationFilter) => {
    const idx = FILTERS.indexOf(current);
    let next: number | undefined;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      next = (idx + 1) % FILTERS.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      next = (idx - 1 + FILTERS.length) % FILTERS.length;
    }
    if (next !== undefined) {
      const nextFilter = FILTERS[next];
      if (!nextFilter) return;
      e.preventDefault();
      onFilterChange(nextFilter);
      const sibling = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next];
      sibling?.focus();
    }
  };

  return (
    <div className="sticky top-0 z-10 flex flex-col gap-2 border-b border-border bg-background px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">Notifications</h3>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            className={cn(
              'inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
            )}
            aria-label="Notification settings"
            onClick={onOpenSettings}
          >
            <Settings className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            className={cn(
              'text-xs font-semibold underline-offset-2 transition-colors',
              canMarkAll && !isMarkingAll
                ? 'cursor-pointer text-foreground underline decoration-foreground/50 decoration-2 hover:text-primary hover:decoration-primary/60'
                : 'cursor-not-allowed text-muted-foreground opacity-50',
            )}
            disabled={!canMarkAll || isMarkingAll}
            onClick={onMarkAllAsRead}
          >
            Mark all as read
          </button>
        </div>
      </div>
      <div
        className="flex rounded-lg border border-border bg-muted/30 p-0.5"
        role="tablist"
        aria-label="Notification filter"
      >
        <button
          type="button"
          role="tab"
          tabIndex={filter === 'all' ? 0 : -1}
          aria-selected={filter === 'all'}
          className={cn(
            'flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
            filter === 'all'
              ? 'bg-background font-semibold text-foreground shadow-sm ring-1 ring-border'
              : 'text-muted-foreground hover:text-foreground',
          )}
          onClick={() => onFilterChange('all')}
          onKeyDown={(e) => handleTabKeyDown(e, 'all')}
        >
          All
        </button>
        <button
          type="button"
          role="tab"
          tabIndex={filter === 'unread' ? 0 : -1}
          aria-selected={filter === 'unread'}
          className={cn(
            'flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
            filter === 'unread'
              ? 'bg-background font-semibold text-foreground shadow-sm ring-1 ring-border'
              : 'text-muted-foreground hover:text-foreground',
          )}
          onClick={() => onFilterChange('unread')}
          onKeyDown={(e) => handleTabKeyDown(e, 'unread')}
        >
          Unread
        </button>
      </div>
    </div>
  );
}
